
import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator';
import {TakipTalebiEntity} from "@/entity/TakipTalebiEntity";

interface VariableInfo {
  full: string;
  name: string;
  startPos: number;
  endPos: number;
}

@Component
export default class TakipTalebiDegistirme extends Vue {
  @Prop({ type: String, default: '' }) readonly value!: string;
  @Prop() takipTalebi!:TakipTalebiEntity
  @Ref('textareaEl') readonly textareaEl!: any;

  metinDisabled = true
  textContent = '';
  originalContent = '';
  initialEditContent = '';
  lastValidContent = '';
  variables: VariableInfo[] = [];
  variableRegex = /\{\{([^}]+)\}\}/g;
  // Kullanıcının metinden çıkarabileceği değişkenler: korunmaz ama tek parça davranır,
  // yarım kalırsa backend yerine koyamaz ve metne "{{faizAciklamasi}" gibi basılır.
  silinebilirDegiskenler = ['faizAciklamasi'];
  isProcessing = false;
  isFirstFocus = true;
  isMounted = false;

  mounted() {
    this.isMounted = true;
    this.initializeContent();
  }

  initializeContent() {
    // Önce value prop'unu kontrol et
    if (this.value) {
      this.textContent = this.value;
      this.originalContent = this.value;
      this.lastValidContent = this.value;
    }
    // Sonra takipTalebi içeriğini kontrol et
    else if (this.takipTalebi) {
      if (this.takipTalebi.ozel_takip_talebi_metni) {
        this.textContent = this.takipTalebi.ozel_takip_talebi_metni;
        this.originalContent = this.takipTalebi.ozel_takip_talebi_metni;
        this.lastValidContent = this.takipTalebi.ozel_takip_talebi_metni;
      } else if (this.takipTalebi.takip_alt_tipi?.takip_talebi_metni) {
        const defaultMetin = this.takipTalebi.takip_alt_tipi.takip_talebi_metni;
        this.textContent = defaultMetin;
        this.originalContent = defaultMetin;
        this.lastValidContent = defaultMetin;

        // Parent component'e değişikliği bildir
        this.$nextTick(() => {
          if (this.takipTalebi.takip_alt_tipi) {
            this.takipTalebi.takip_alt_tipi.ozel_takip_talebi_metni = defaultMetin;
          }
          this.$emit('input', defaultMetin);
        });
      }
    }

    this.findVariables();
  }

  @Watch('value', { immediate: true })
  onValueChange(newVal: string): void {
    if (!this.isProcessing && this.isMounted && newVal !== this.textContent) {
      this.originalContent = newVal || '';
      this.textContent = this.originalContent;
      this.lastValidContent = this.originalContent;
      this.findVariables();
    }
  }

  @Watch('takipTalebi', { deep: true, immediate: true })
  onTakipTalebiChange(): void {
    if (this.isMounted && !this.textContent && !this.value) {
      this.initializeContent();
    }
  }

  metinDuzenle() {
    this.metinDisabled = !this.metinDisabled;
  }

  findVariables(): void {
    this.variables = [];
    const content = this.originalContent || this.textContent;
    if (!content) return;

    let match;
    this.variableRegex.lastIndex = 0;
    while ((match = this.variableRegex.exec(content)) !== null) {
      if (this.silinebilirDegiskenler.includes(match[1].trim())) continue;
      this.variables.push({
        full: match[0],
        name: match[1],
        startPos: match.index,
        endPos: match.index + match[0].length
      });
    }
  }

  handleFocus(): void {
    if (this.isFirstFocus) {
      this.initialEditContent = this.textContent;
      this.isFirstFocus = false;
    }
  }

  handleTextChange(): void {
    if (this.isProcessing) return;

    this.isProcessing = true;

    const currentContent = this.textContent;
    let isValid = true;

    for (const variable of this.variables) {
      if (!currentContent.includes(variable.full)) {
        isValid = false;
        break;
      }
    }

    if (isValid && this.silinebilirDegiskenBozulduMu(this.lastValidContent, currentContent)) {
      isValid = false;
    }

    if (isValid) {
      this.lastValidContent = currentContent;
      this.$emit('input', currentContent);

      // takipTalebi güncellemesi
      if (this.takipTalebi) {
        this.takipTalebi.ozel_takip_talebi_metni = currentContent;
        if (this.takipTalebi.takip_alt_tipi) {
          this.takipTalebi.takip_alt_tipi.ozel_takip_talebi_metni = currentContent;
        }
      }
    } else {
      this.textContent = this.lastValidContent;

      this.$nextTick(() => {
        const textarea = this.textareaEl?.$refs?.input;
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(this.textContent.length, this.textContent.length);
        }
      });
    }

    this.isProcessing = false;
  }

  handleKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLTextAreaElement;
    const selStart = target.selectionStart;
    const selEnd = target.selectionEnd;

    if ((event.key === 'Backspace' || event.key === 'Delete') &&
        this.isSelectionInVariableRegion(selStart, selEnd)) {
      event.preventDefault();
      return;
    }

    if (event.key.length === 1 && this.isSelectionInVariableRegion(selStart, selEnd)) {
      event.preventDefault();
      return;
    }

    this.silinebilirDegiskeniButunTut(event, target);
  }

  // Backspace/Delete veya kısmi seçim üzerinde yapılan düzenlemede seçimi değişkenin tamamına
  // genişletir, böylece tarayıcının kendi silme/yazma işlemi değişkeni bütün olarak kaldırır.
  // İmleç değişkenin içindeyken yazma ve yapıştırma engellenir.
  silinebilirDegiskeniButunTut(event: KeyboardEvent, target: HTMLTextAreaElement): void {
    const kisayol = event.ctrlKey || event.metaKey;
    const silme = event.key === 'Backspace' || event.key === 'Delete';
    const yazma = event.key.length === 1 && !kisayol;
    const kesYapistir = kisayol && ['x', 'v'].includes(event.key.toLowerCase());
    if (!silme && !yazma && !kesYapistir) return;

    let start = target.selectionStart;
    let end = target.selectionEnd;
    if (start === null || end === null) return;

    for (const aralik of this.silinebilirAraliklar(this.textContent)) {
      if (start === end) {
        const icinde = start > aralik.start && start < aralik.end;
        if ((event.key === 'Backspace' && start > aralik.start && start <= aralik.end)
            || (event.key === 'Delete' && start >= aralik.start && start < aralik.end)) {
          start = aralik.start;
          end = aralik.end;
        } else if (icinde) {
          event.preventDefault();
          return;
        }
      } else if (start < aralik.end && end > aralik.start) {
        start = Math.min(start, aralik.start);
        end = Math.max(end, aralik.end);
      }
    }

    if (start !== target.selectionStart || end !== target.selectionEnd) {
      target.setSelectionRange(start, end);
    }
  }

  silinebilirAraliklar(text: string): { start: number, end: number }[] {
    const araliklar: { start: number, end: number }[] = [];
    const regex = /\{\{([^{}]+)\}\}/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (this.silinebilirDegiskenler.includes(match[1].trim())) {
        araliklar.push({start: match.index, end: match.index + match[0].length});
      }
    }
    return araliklar;
  }

  // Klavye dışı düzenlemeler (sağ tık kes/yapıştır, sürükle-bırak) keydown'dan geçmez:
  // değişen bölge silinebilir bir değişkenle kısmen kesişiyorsa değişken bozulmuştur.
  silinebilirDegiskenBozulduMu(eski: string, yeni: string): boolean {
    const araliklar = this.silinebilirAraliklar(eski);
    if (!araliklar.length) return false;

    const kisa = Math.min(eski.length, yeni.length);
    let onek = 0;
    while (onek < kisa && eski[onek] === yeni[onek]) onek++;
    let sonek = 0;
    while (sonek < kisa && eski[eski.length - 1 - sonek] === yeni[yeni.length - 1 - sonek]) sonek++;

    // Tekrarlayan karakterlerde değişen bölgenin yeri belirsizdir (ör. bitişik "}}{{");
    // en küçük değişen bölgenin olası her konumu denenir, biri değişkeni bölmüyorsa geçerlidir.
    const eslesen = Math.min(onek + sonek, kisa);
    for (let bas = Math.max(0, eslesen - sonek); bas <= Math.min(onek, eslesen); bas++) {
      const son = eski.length - (eslesen - bas);
      const bolmuyor = araliklar.every(a => son <= a.start || bas >= a.end || (bas <= a.start && son >= a.end));
      if (bolmuyor) return false;
    }
    return true;
  }

  isSelectionInVariableRegion(start: number | null, end: number | null): boolean {
    if (start === null || end === null) return false;

    if (start === end) {
      for (const variable of this.variables) {
        if (this.getVariablePositionInCurrentText(variable, start)) {
          return true;
        }
      }
    } else {
      for (const variable of this.variables) {
        const pos: any = this.getVariablePositionInCurrentText(variable);
        if (!pos) continue;

        if ((start < pos.end && end > pos.start)) {
          return true;
        }
      }
    }

    return false;
  }

  getVariablePositionInCurrentText(variable: VariableInfo, position: number | null = null): {start: number, end: number} | boolean | null {
    const index = this.textContent.indexOf(variable.full);
    if (index === -1) return null;

    const start = index;
    const end = index + variable.full.length;

    if (position !== null) {
      return position >= start && position <= end;
    }

    return { start, end };
  }
}
